import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import BookingProgress from "./components/BookingProgress";
import Step1Service from "./components/Step1Service";
import Step3Items from "./components/Step3Items";
import Step2BinCollection from "./components/Step2BinCollection";
import Step3Combined from "./components/Step3Combined";
import BookingSuccess from "./components/BookingSuccess";
import { useWCServices } from "../../hooks/useWCProducts";
import { ordersApi } from "../../lib/api";
import { useCMSPage } from "../../context/CMSContext";
import { timeSlots } from "../../mocks/bookingData";

export interface CartItem {
  id: string;
  label: string;
  quantity: number;
  unitPrice: number;
}

export interface BookingState {
  serviceSlug: string;
  serviceTitle: string;
  stepType: "load" | "items" | "rooms" | "schedule" | "bin" | "";
  selectedOption: string;
  selectedOptionLabel: string;
  selectedOptionPrice: number;
  cartItems: CartItem[];
  postcode: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  county: string;
  houseNumber: string;
  date: string;
  timeSlot: string;
  timeSlotLabel: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
  wasteDescription: string;
}

const initialState: BookingState = {
  serviceSlug: "",
  serviceTitle: "",
  stepType: "",
  selectedOption: "",
  selectedOptionLabel: "",
  selectedOptionPrice: 0,
  cartItems: [],
  postcode: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  county: "",
  houseNumber: "",
  date: "",
  timeSlot: "",
  timeSlotLabel: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  notes: "",
  wasteDescription: "",
};

/** Callout is waived when items subtotal reaches this threshold */
export const CALLOUT_MINIMUM = 99;

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep]         = useState(1);
  const [booking, setBooking]   = useState<BookingState>(initialState);
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [wcOrderId, setWcOrderId]   = useState<number | null>(null);

  const navigate = useNavigate();
  const { services } = useWCServices();
  const { data: bookingCMS } = useCMSPage("booking");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ui: any = (bookingCMS as any)?.ui ?? {};

  useEffect(() => {
    const serviceParam  = searchParams.get("service");
    const postcodeParam = searchParams.get("postcode");
    const updates: Partial<BookingState> = {};

    if (postcodeParam) updates.postcode = postcodeParam.toUpperCase();
    if (serviceParam) {
      const service = services.find((s) => s.slug === serviceParam);
      if (service) {
        updates.serviceSlug  = service.slug;
        updates.serviceTitle = service.title;
        updates.stepType     = service.stepType;
        setBooking((prev) => ({ ...prev, ...updates }));
        setStep(2);
        return;
      }
    }
    if (Object.keys(updates).length > 0) {
      setBooking((prev) => ({ ...prev, ...updates }));
    }
  }, [searchParams, services]);

  const completedSteps = Array.from({ length: step - 1 }, (_, i) => i + 1);

  const updateBooking = (updates: Partial<BookingState>) =>
    setBooking((prev) => ({ ...prev, ...updates }));

  const goNext = () => { setStep((s) => Math.min(s + 1, 3)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const goBack = () => { setStep((s) => Math.max(s - 1, 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  /**
   * Callout charge logic:
   *   - Only added when the service has a calloutCharge configured
   *   - Only added when items/options subtotal is BELOW £99
   *   - At £99+ the callout is waived entirely — never shown, never added to total
   */
  const getItemsSubtotal = () => {
    if (booking.stepType === "items" || booking.stepType === "bin") {
      return booking.cartItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
    }
    return booking.selectedOptionPrice + booking.cartItems.reduce((sum, c) => sum + c.quantity * c.unitPrice, 0);
  };

  const getTotal = () => {
    const serviceConfig = services.find((s) => s.slug === booking.serviceSlug);
    const subtotal      = getItemsSubtotal();
    const callout       = serviceConfig?.calloutCharge && subtotal < CALLOUT_MINIMUM
      ? serviceConfig.calloutCharge
      : 0;
    const slotSurcharge = timeSlots.find((t) => t.id === booking.timeSlot)?.price ?? 0;
    return subtotal + callout + slotSurcharge;
  };

  /** Cash — create WC order (on-hold), send confirmation email, show success screen */
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await ordersApi.create({
        ...booking,
        cartItems: booking.cartItems.map((c) => ({
          id:        c.id,
          label:     c.label,
          quantity:  c.quantity,
          unitPrice: c.unitPrice,
        })),
      });
      setWcOrderId(result.order_id);
      setSubmitted(true);
    } catch (err) {
      setSubmitError((err as Error)?.message ?? "Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  /** Card — navigate to /checkout passing booking state; checkout page handles Stripe */
  const handleStripeCheckout = () => {
    navigate("/checkout", {
      state: { booking, total: getTotal() },
    });
  };

  if (submitted) {
    return <BookingSuccess booking={booking} total={getTotal()} wcOrderId={wcOrderId} />;
  }

  return (
    <div className="min-h-screen bg-cl-tint">
      <nav className="bg-white border-b-4 border-cl-dark sticky top-0 z-50">
        <div className="max-w-screen-xl mx-auto px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-1 cursor-pointer">
            <span className="text-2xl font-black tracking-tight text-cl-dark">
              Clear<span className="text-cl-green">io</span>
            </span>
            <span className="w-2.5 h-2.5 rounded-full bg-cl-green mb-4 inline-block border-2 border-cl-dark"></span>
          </a>
          <div className="flex items-center gap-5">
            <a href="tel:03335775445" className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-cl-dark hover:text-cl-green transition-colors">
              <i className="ri-phone-fill text-cl-green text-sm"></i>
              03335 775445
            </a>
            <a href="/" className="flex items-center gap-1.5 text-sm font-bold text-cl-dark/50 hover:text-cl-dark transition-colors cursor-pointer">
              <i className="ri-close-line"></i>
              <span className="hidden sm:inline">Exit</span>
            </a>
          </div>
        </div>
      </nav>

      <div className="max-w-screen-xl mx-auto px-8 pt-12 pb-20">
        <div className="mb-10">
          <BookingProgress
            currentStep={step}
            completedSteps={completedSteps}
            onStepClick={(s) => { if (completedSteps.includes(s)) setStep(s); }}
          />
        </div>

        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl text-red-700 font-semibold text-sm">
            <i className="ri-error-warning-line mr-2"></i>{submitError}
          </div>
        )}

        {step === 1 && (
          <Step1Service booking={booking} onUpdate={updateBooking} onNext={goNext} services={services} />
        )}
        {step === 2 && booking.stepType === "bin" && (
          <Step2BinCollection booking={booking} onUpdate={updateBooking} onNext={goNext} onBack={goBack} services={services} />
        )}
        {step === 2 && booking.stepType !== "bin" && (
          <Step3Items booking={booking} onUpdate={updateBooking} onNext={goNext} onBack={goBack} services={services} />
        )}
        {step === 3 && (
          <Step3Combined
            booking={booking}
            onUpdate={updateBooking}
            onBack={goBack}
            onSubmit={handleSubmit}
            onStripeCheckout={handleStripeCheckout}
            total={getTotal()}
            submitting={submitting}
            bookCTA={ui?.book_cta ?? "Confirm Booking"}
            totalLabel={ui?.total_label ?? "Total (inc. VAT)"}
          />
        )}
      </div>

      <div className="bg-white border-t-2 border-transparent py-4">
        <div className="max-w-screen-xl mx-auto px-8 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-400 font-semibold">
            Waste Carrier Licence: CBDU 634440 &nbsp;·&nbsp; &copy; Cleario 2026
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-xs text-gray-400 hover:text-cl-dark font-semibold cursor-pointer">Terms</a>
            <a href="#" className="text-xs text-gray-400 hover:text-cl-dark font-semibold cursor-pointer">Privacy</a>
            <a href="#" className="text-xs text-gray-400 hover:text-cl-dark font-semibold cursor-pointer">Cookies</a>
          </div>
        </div>
      </div>
    </div>
  );
}